import React from 'react';
import { StudioPreviewCurrencies } from 'src/components/star-grid/components/StudioPreviewCurrencies';
import { ReportSelector } from 'src/components/star-grid/components/ReportSelector';
import { StudioFieldEditor } from 'src/components/star-grid/components/StudioFieldEditor';
import { StudioParameterPill } from 'src/components/star-grid/components/StudioParameterPill';
import { useStudioPreviewFilters } from 'src/components/star-grid/hooks/useStudioPreviewFilters';
import type { ReportStudioState } from 'src/components/star-grid/hooks/useReportStudio';
import { studioSelectOptions, studioSelectedOption } from 'src/components/star-grid/utils/studioSelect';
import { PNL_SCALE_OPTIONS } from 'src/components/star-grid/utils/pnlGrid';
import styles from 'src/components/star-grid/components/styles/SavedReportFilters.module.scss';
export const StudioPreviewFilters: React.FC<{ state: ReportStudioState }> = ({ state }) => {
  const filters = useStudioPreviewFilters(state);
  return (
    <section
      id="stargrid-report-filters"
      data-testid="studio-preview-filters"
      data-filter-preview
      className={styles.filters}
      aria-label={state.text('reportFilters')}
    >
      <ReportSelector
        label={state.text('reportVersion')}
        controlId="studio-preview-version"
        control={{
          expandToViewport: true,
          selectedOption: studioSelectedOption(state.template.catalog.versions, state.draft.definition.default_version_id),
          options: studioSelectOptions(state.template.catalog.versions),
          onChange: ({ detail }) => state.definition({ default_version_id: detail.selectedOption.value ?? '' })
        }}
      />
      <ReportSelector
        label={state.text('reportDeck')}
        controlId="studio-preview-deck"
        control={{
          expandToViewport: true,
          selectedOption: { value: state.draft.definition.active_deck, label: state.draft.definition.active_deck },
          options: state.draft.definition.deck_types.map((value) => ({ value, label: value })),
          onChange: ({ detail }) => state.definition({ active_deck: detail.selectedOption.value ?? '' })
        }}
      />
      {filters.parameters.map((parameter) => (
        <StudioParameterPill key={parameter.key} compact parameter={parameter} state={state} />
      ))}
      {state.deck.filters.map((field, index) => (
        <StudioFieldEditor
          key={field.id}
          compact
          shelf="filters"
          field={field}
          dataset={state.dataset}
          lineSets={state.deck.line_sets}
          text={state.text}
          onChange={(next) => state.updateField('filters', next)}
          onRemove={() => state.removeField('filters', field.id)}
          onMove={(offset) => state.moveField('filters', index, offset)}
        />
      ))}
      <ReportSelector
        label={state.text('reportScale')}
        controlId="studio-preview-scale"
        control={{
          expandToViewport: true,
          selectedOption: { value: state.scaleId, label: state.text(PNL_SCALE_OPTIONS.find((scale) => scale.id === state.scaleId)!.labelKey) },
          options: PNL_SCALE_OPTIONS.map((scale) => ({ value: scale.id, label: state.text(scale.labelKey) })),
          onChange: ({ detail }) => state.setScaleId(detail.selectedOption.value ?? PNL_SCALE_OPTIONS[0].id)
        }}
      />
      {filters.currencyParameters.map((parameter) => (
        <StudioParameterPill key={parameter.key} compact parameter={parameter} state={state} />
      ))}
      {!filters.currencyParameters.length && filters.scenarios.length > 0 && <StudioPreviewCurrencies state={state} filters={filters} />}
    </section>
  );
};
